import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

type TUseOverflowAwareActionMenuOptions = {
  showMenu: boolean;
  footerHeight?: number;
  viewportPadding?: number;
  gap?: number;
  fallbackWidth?: number;
  fallbackHeight?: number;
  anchorPosition?: { x: number; y: number } | null;
};

export const useOverflowAwareActionMenu = ({
  showMenu,
  footerHeight = 52,
  viewportPadding = 12,
  gap = 8,
  fallbackWidth = 210,
  fallbackHeight = 300,
  anchorPosition = null,
}: TUseOverflowAwareActionMenuOptions) => {
  const actionMenuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  const alignMenu = useCallback(() => {
    if (!showMenu || !actionMenuRef.current || typeof window === 'undefined') return;

    const menuWidth = actionMenuRef.current.offsetWidth || fallbackWidth;
    const menuHeight = actionMenuRef.current.offsetHeight || fallbackHeight;
    let x = viewportPadding;
    let y = viewportPadding;

    if (anchorPosition) {
      x = Math.min(Math.max(anchorPosition.x, viewportPadding), window.innerWidth - menuWidth - viewportPadding);
      const spaceBelow = window.innerHeight - footerHeight - viewportPadding - anchorPosition.y;
      y =
        spaceBelow >= menuHeight
          ? anchorPosition.y
          : Math.max(viewportPadding, anchorPosition.y - menuHeight - gap);
    } else {
      if (!triggerRef.current) return;
      const triggerRect = triggerRef.current.getBoundingClientRect();
      x = Math.min(
        Math.max(triggerRect.right - menuWidth, viewportPadding),
        window.innerWidth - menuWidth - viewportPadding,
      );
      const spaceBelow = window.innerHeight - footerHeight - viewportPadding - triggerRect.bottom;
      y =
        spaceBelow >= menuHeight
          ? triggerRect.bottom + gap
          : Math.max(viewportPadding, triggerRect.top - menuHeight - gap);
    }

    setMenuPosition((prev) => (prev.x === x && prev.y === y ? prev : { x, y }));
  }, [anchorPosition, fallbackHeight, fallbackWidth, footerHeight, gap, showMenu, viewportPadding]);

  useLayoutEffect(() => {
    alignMenu();
  }, [alignMenu, showMenu]);

  useEffect(() => {
    if (!showMenu) return;
    const handleReposition = () => alignMenu();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [alignMenu, showMenu]);

  return {
    actionMenuRef,
    triggerRef,
    menuPosition,
    setMenuPosition,
    alignMenu,
  };
};
